import {useCallback,useEffect,useRef,useState} from "react";
import type {PlatformApiClient} from "../api";
import {PlatformApiError} from "../api";
import type {OrganizationContext} from "../types";
import type {
  HotelProperty,HotelPolicy,HotelInboxSummary,HotelConversationDetail,
  HotelDraft,HotelBookingDestination,
} from "./types";

type Section="home"|"conversations"|"knowledge"|"settings";
function sectionFromPath(path:string):Section {
  const segment=path.replace(/^\/+/, "").split("/")[0];
  return segment==="conversations"||segment==="knowledge"||segment==="settings"?segment:"home";
}
function errorMessage(e:unknown):string {
  return e instanceof PlatformApiError ? e.message : "No se pudo completar la operación.";
}
function stamp():string{return new Date().toISOString()}
function key():string{return "hotel-"+crypto.randomUUID()}
export function HospitalityProduct({organization,nestedPath,api,onExpired}:{
  organization:OrganizationContext;
  nestedPath:string;api:PlatformApiClient;onExpired:()=>void;
}){
  const org=organization.organization.id;
  const section=sectionFromPath(nestedPath);
  const [properties,setProperties]=useState<readonly HotelProperty[]>([]);
  const [propertyId,setPropertyId]=useState("");
  const [busy,setBusy]=useState(false);
  const [error,setError]=useState<string|null>(null);
  const [inbox,setInbox]=useState<readonly HotelInboxSummary[]>([]);
  const [inboxRestricted,setInboxRestricted]=useState(false);
  const [policies,setPolicies]=useState<readonly HotelPolicy[]>([]);
  const [destination,setDestination]=useState<HotelBookingDestination|null>(null);
  const [conversationId,setConversationId]=useState("");
  const [detail,setDetail]=useState<HotelConversationDetail["conversation"]|null>(null);
  const [draft,setDraft]=useState<HotelDraft|null>(null);
  const [reply,setReply]=useState("");
  const [confirmed,setConfirmed]=useState(false);
  const [locale,setLocale]=useState("es");
  const [policyKey,setPolicyKey]=useState("check_in");
  const [policyAnswer,setPolicyAnswer]=useState("");
  const [policyLocale,setPolicyLocale]=useState("es");
  const [policyStart,setPolicyStart]=useState(stamp().slice(0,10));
  const [policyReview,setPolicyReview]=useState<{id:string;version:number;answer:string}|null>(null);
  const onExpiredRef=useRef(onExpired);
  onExpiredRef.current=onExpired;

  const fail=useCallback((cause:unknown)=>{
    if(cause instanceof PlatformApiError&&cause.status===401)onExpiredRef.current();
    setError(errorMessage(cause));
  },[]);
  const loadProperties=useCallback(async()=>{
    try{
      setError(null);
      const next=await api.hospitalityGet<{properties:HotelProperty[]}>(org,"properties");
      setProperties(next.properties);
      setPropertyId(prev=>next.properties.some(p=>p.id===prev)?prev:(next.properties[0]?.id??""));
    }catch(cause){fail(cause)}
  },[api,org,fail]);
  const loadHotel=useCallback(async()=>{
    if(!propertyId)return;
    try{
      const base="properties/"+propertyId;
      const [inboxResult,policyResult,bookingResult]=await Promise.allSettled([
        api.hospitalityGet<{conversations:HotelInboxSummary[]}>(org,base+"/inbox"),
        api.hospitalityGet<{policies:HotelPolicy[]}>(org,base+"/policies"),
        api.hospitalityGet<{destination:HotelBookingDestination|null}>(org,base+"/booking-destination"),
      ]);
      setInboxRestricted(inboxResult.status==="rejected"&&
        inboxResult.reason instanceof PlatformApiError&&inboxResult.reason.status===403);
      setInbox(inboxResult.status==="fulfilled"?inboxResult.value.conversations:[]);
      setPolicies(policyResult.status==="fulfilled"?policyResult.value.policies:[]);
      const bookingData=bookingResult.status==="fulfilled"?bookingResult.value.destination:null;
      setDestination(bookingData);
      if(policyResult.status==="rejected")throw policyResult.reason;
      if(bookingResult.status==="rejected"&&
        !(bookingResult.reason instanceof PlatformApiError&&bookingResult.reason.status===403))
        throw bookingResult.reason;
      if(inboxResult.status==="rejected"&&
        !(inboxResult.reason instanceof PlatformApiError&&inboxResult.reason.status===403))
        throw inboxResult.reason;
    }catch(cause){fail(cause)}
  },[api,org,propertyId,fail]);
  const loadConversation=useCallback(async(id:string)=>{
    if(!propertyId||!id)return;
    try{
      const response=await api.hospitalityGet<HotelConversationDetail>(
        org,"properties/"+propertyId+"/inbox/"+id);
      setDetail(response.conversation);
    }catch(cause){fail(cause)}
  },[api,org,propertyId,fail]);
  useEffect(()=>{void loadProperties()},[loadProperties]);
  useEffect(()=>{setConversationId("");setDetail(null);setDraft(null);setReply("");void loadHotel()},[loadHotel]);
  useEffect(()=>{if(conversationId)void loadConversation(conversationId)},[conversationId,loadConversation]);

  async function perform(work:()=>Promise<void>){
    setBusy(true);setError(null);
    try{await work()}catch(cause){fail(cause)}
    finally{setBusy(false)}
  }
  const root="properties/"+propertyId;
  async function generateDraft(){
    if(!conversationId)return;
    await perform(async()=>{
      const response=await api.hospitalityPost<{result:HotelDraft}>(org,root+
        "/conversations/"+conversationId+"/draft",{locale});
      setDraft(response.result);
      setReply(response.result.status==="draft"?response.result.draftText??"":"");
      setConfirmed(false);
    });
  }
  async function control(mode:"human"|"ai"){
    if(!conversationId||!detail)return;
    await perform(async()=>{
      await api.hospitalityPost(org,root+"/inbox/"+conversationId+"/control",{
        mode,expectedRevision:detail.operator.revision,occurredAt:stamp(),
      },key());
      await loadConversation(conversationId);
      setConfirmed(false);
    });
  }
  async function sendReply(){
    if(!conversationId||!detail||!confirmed)return;
    const last=detail.messages.at(-1);
    if(last?.direction!=="inbound"){setError("Hay que revisar el último mensaje antes de enviar.");return}
    await perform(async()=>{
      await api.hospitalityPost(org,root+"/inbox/"+conversationId+"/reply",{
        body:reply,sendNow:true,expectedInboundMessageId:last.id,
      },key());
      setReply("");setConfirmed(false);setDraft(null);
      await loadConversation(conversationId);
      await loadHotel();
    });
  }
  async function disableBooking(){
    if(!destination)return;
    await perform(async()=>{
      await api.hospitalityPost(org,root+"/booking-destination/disable",
        {expectedVersion:destination.version});
      await loadHotel();
    });
  }
  async function createPolicy(){
    await perform(async()=>{
      await api.hospitalityPost(org,root+"/policies",{
        policyKey,locale:policyLocale,audience:"public",
        answer:policyAnswer,validFrom:policyStart,validUntil:null,
      });
      setPolicyAnswer("");await loadHotel();
    });
  }
  async function reviewPolicy(policy:HotelPolicy){
    await perform(async()=>{
      const result=await api.hospitalityGet<{editorial:{revisions:{state:string;answer:string}[]}}>(
        org,root+"/policies/"+policy.id);
      const pending=result.editorial.revisions.find(r=>r.state==="draft");
      if(!pending)throw new Error("La política no tiene borrador pendiente.");
      setPolicyReview({id:policy.id,version:policy.version,answer:pending.answer});
    });
  }
  async function approvePolicy(policy:HotelPolicy){
    if(policyReview?.id!==policy.id||policyReview.version!==policy.version){
      setError("Primero revisá la respuesta exacta antes de aprobar.");return;
    }
    await perform(async()=>{
      await api.hospitalityPost(org,root+"/policies/"+policy.id+"/approve",
        {expectedVersion:policy.version});
      await loadHotel();setPolicyReview(null);
    });
  }

  return <main className="cd-hospitality-workspace" id="main-content">
    <header className="cd-page-header">
      <p className="cd-page-header__eyebrow">ClickDialog Hospitality · L0 supervisado</p>
      <h1 className="cd-page-header__title">{section==="conversations"?"Conversaciones":section==="knowledge"?"Conocimiento":section==="settings"?"Configuración":"Recepción hotelera"}</h1>
      <p>Las respuestas requieren revisión humana. No confirmamos disponibilidad, precio ni reservas.</p>
      <div style={{display:"flex",gap:12,flexWrap:"wrap",alignItems:"center"}}>
        <label htmlFor="hotel-property-selector">Hotel</label>
        <select id="hotel-property-selector" value={propertyId} onChange={e=>setPropertyId(e.target.value)}>
          {properties.map(p=><option key={p.id} value={p.id}>{p.displayName}</option>)}
        </select>
        <button type="button" onClick={()=>void loadHotel()} disabled={busy||!propertyId}>Actualizar</button>
      </div>
    </header>
    {error&&<p role="alert" className="inline-alert">{error}</p>}
    {!propertyId&&<p role="status">No hay hoteles habilitados para esta cuenta. Solicitá la activación a ClickDialog.</p>}
    {propertyId&&section==="home"&&<section aria-label="Resumen del hotel">
      <h2>Estado operativo</h2>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(200px,1fr))",gap:16}}>
        <article><h3>Conversaciones</h3><strong>{inbox.length}</strong><p>Visibles para esta propiedad</p></article>
        <article><h3>Políticas</h3><strong>{policies.length}</strong><p>Fuentes editoriales del hotel</p></article>
        <article><h3>Reserva directa</h3><strong>{destination?.status==="verified"?"Enlace verificado":"Sin enlace activo"}</strong></article>
      </div>
      <p>ClickDialog no sustituye al PMS ni confirma una reserva sin el motor oficial.</p>
    </section>}
    {propertyId&&section==="conversations"&&<section aria-label="Bandeja de conversaciones">
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(280px,1fr))",gap:20}}>
        <div>
          <h2>Inbox</h2>
          {inboxRestricted&&<p role="status">Tu permiso actual no permite consultar conversaciones.</p>}
          {inboxRestricted?null:inbox.length===0?<p>No hay mensajes WhatsApp asignados a este hotel.</p>:<ul>
            {inbox.map(item=><li key={item.conversation_id}>
              <button type="button" aria-pressed={conversationId===item.conversation_id}
                onClick={()=>{setConversationId(item.conversation_id);setDraft(null);setReply("");setConfirmed(false)}}>
                {item.last_guest_message?.slice(0,110)??"Conversación sin mensajes"}
              </button>
              <small> {item.status} · {item.last_message_at??item.updated_at}</small>
            </li>)}
          </ul>}
        </div>
        <div>
          <h2>Conversación</h2>
          {!detail?<p>Seleccioná una conversación.</p>:<>
            <p>Estado: {detail.operator.lifecycle}. Control: <strong>{detail.operator.controlMode==="human"?"Humano":"Agente"}</strong>.</p>
            <div role="log" aria-label="Mensajes del huésped" style={{maxHeight:330,overflow:"auto"}}>
              {detail.messages.map(m=><p key={m.id}>
                <strong>{m.direction==="inbound"?"Huésped":"Recepción"}:</strong> {m.body}
              </p>)}
            </div>
            <label htmlFor="hotel-locale">Idioma de la respuesta</label>
            <select id="hotel-locale" value={locale} onChange={e=>setLocale(e.target.value)}>
              <option value="es">Español</option><option value="fr">Français</option><option value="en">English</option>
            </select>
            <div style={{display:"flex",gap:8,flexWrap:"wrap",marginBlock:12}}>
              <button type="button" onClick={()=>void generateDraft()} disabled={busy||detail.operator.controlMode==="human"}>Generar borrador</button>
              {detail.operator.controlMode!=="human"
                ?<button type="button" onClick={()=>void control("human")} disabled={busy}>Tomar control humano</button>
                :<button type="button" onClick={()=>void control("ai")} disabled={busy}>Devolver control</button>}
            </div>
            {draft&&<p role="status">Borrador: {draft.status}. {draft.reason??""} No se envió ningún mensaje.</p>}
            <label htmlFor="hospitality-reviewed-message">Respuesta revisada por recepción</label>
            <textarea id="hospitality-reviewed-message" rows={5} maxLength={4096}
              value={reply} onChange={e=>{setReply(e.target.value);setConfirmed(false)}}/>
            <label style={{display:"block",marginBlock:10}}>
              <input type="checkbox" checked={confirmed} onChange={e=>setConfirmed(e.target.checked)}/>
              Revisé el texto y autorizo este mensaje WhatsApp
            </label>
            <button type="button" disabled={busy||!confirmed||!reply.trim()||detail.operator.controlMode!=="human"}
              onClick={()=>void sendReply()}>Enviar respuesta aprobada</button>
          </>}
        </div>
      </div>
    </section>}
    {propertyId&&section==="knowledge"&&<section aria-label="Políticas del hotel">
      <h2>Políticas y fuentes</h2>
      {policies.length===0?<p>No hay políticas cargadas.</p>:<ul>
        {policies.map(p=><li key={p.id}>
          <strong>{p.policyKey}</strong> · {p.locale} · {p.status} · versión {p.version}
          {p.approvedRevision===null&&p.status==="active"&&
            <button type="button" disabled={busy} onClick={()=>void reviewPolicy(p)}>Revisar borrador</button>}
          {policyReview?.id===p.id&&
            <div style={{padding:12,border:"1px solid currentColor"}}>
              <p><strong>Respuesta pendiente de aprobación:</strong> {policyReview.answer}</p>
              <button type="button" disabled={busy} onClick={()=>void approvePolicy(p)}>Aprobar este texto</button>
            </div>}
        </li>)}
      </ul>}
      <form onSubmit={e=>{e.preventDefault();void createPolicy()}}>
        <h3>Nueva política — se guardará como borrador</h3>
        <label htmlFor="policy-key">Tema</label>
        <select id="policy-key" value={policyKey} onChange={e=>setPolicyKey(e.target.value)}>
          {["check_in","check_out","pets","parking","breakfast","children","accessibility","payment","cancellation","no_show"].map(k=>
            <option key={k} value={k}>{k}</option>)}
        </select>
        <label htmlFor="policy-locale">Idioma</label>
        <select id="policy-locale" value={policyLocale} onChange={e=>setPolicyLocale(e.target.value)}>
          <option value="es">Español</option><option value="fr">Français</option><option value="en">English</option>
        </select>
        <label htmlFor="policy-start">Válida desde</label>
        <input id="policy-start" type="date" value={policyStart} onChange={e=>setPolicyStart(e.target.value)} required/>
        <label htmlFor="policy-answer">Respuesta aprobable</label>
        <textarea id="policy-answer" rows={4} minLength={8} maxLength={5000} value={policyAnswer}
          onChange={e=>setPolicyAnswer(e.target.value)} required/>
        <button type="submit" disabled={busy||policyAnswer.trim().length<8}>Guardar borrador</button>
      </form>
    </section>}
    {propertyId&&section==="settings"&&<section aria-label="Reserva directa">
      <h2>Motor oficial de reservas</h2>
      <p>Solo derivamos al enlace HTTPS comprobado del hotel. La disponibilidad y el precio pertenecen al proveedor.</p>
      <p>Estado: <strong>{destination?.status??"Sin configurar"}</strong></p>
      <p>Por seguridad, ClickDialog verifica el enlace oficial antes de activarlo. Solicitá la configuración o actualización a tu operador.</p>
      {destination?.status==="verified"&&
        <p><a href={destination.bookingUrl} target="_blank" rel="noopener noreferrer">Abrir motor oficial de reservas</a></p>}
      {destination?.status==="verified"&&
        <button type="button" disabled={busy} onClick={()=>void disableBooking()}>Desactivar enlace</button>}
    </section>}
  </main>;
}
